<template>
  <div class="trainer-categories tracactr">
    <div
      v-for="cat in options"
      :key="cat.id"
      class="tracactr_item gw_hvr_top"
      :class="{
        'sttrancenter_item_empty tracactr_item_empty': skeleton && !cat.titleVisible,
      }"
    >
      <slot
        :name="`title_${cat.id}`"
        :item="item"
      >
      </slot>
      <div
        v-if="cat.visible"
        class="tracactr_category"
        style="padding: 0 0 0.125rem 0"
        :class="{ 'no-events': skeleton }"
        :data-tst="`trainer_range_category_${cat.id}`"
        @mouseleave="handleContMouseleave"
        @mouseenter="handleContMouseenter"
      >
        <!-- v-show="cat.visible" -->
        <RangeCategoryFilterItem
          v-for="(item, i) in skeleton ? getSkeletonItems() : cat.items"
          :id="`category_${cat.id}_${item.name}`"
          :key="`${cat.id}_${item.name}`"
          :data-tst="`range_filter_${cat.id}_${item.name}`"
          class="tracactr_category_item"
          :style="`
          ${
            cat.hand === item.name
              ? '--clr-text:var(--clr-tracactr-item-active)'
              : '--clr-text: var(--clr-tracactr-item-inactive)'
          }`"
          :readOnly="readOnly"
          :skeleton="skeleton"
          :item="item"
          :useHex="useHex"
          :isLocked="item.is_locked"
          :useTotalOccurenceNormalized="useTotalOccurenceNormalized"
          :isSelected="filterCategories.some(r => r.name === item.name)"
          :hasArrowBtn="hasArrowBtn"
          :hasFilterBtn="hasFilterBtn"
          :arrowBtnDown="hasArrowBtn && allPreviousIncluded(i, cat.items)"
          @arrow:click="toggleRangeCatPlusPrevious(i, cat.items)"
          @filter:click="handleFilterClick(item)"
          @click="handleClick(item)"
          @contextmenu="e => handleContextmenuClick(e, item)"
          @mouseenter="handleMouseenter(item)"
        >
        </RangeCategoryFilterItem>
      </div>
    </div>
  </div>
</template>

<script>
import { throttle } from 'lodash'
import {
  getCategoriesHandIndex,
  getCategoriesDrawsIndex,
  getCategoriesEqBucketsIndex,
  getCategoriesEqBucketsAdvancedIndex,
} from '@/helpers/constants'
import { getVisibleItems } from '@/helpers/solution'
import RangeCategoryFilterItem from '@/components/RangeCategoryFilterItem/RangeCategoryFilterItem'
import screen_mix from '@/mixins/screen_mix'

export default {
  name: 'TrainerRangeCategories',
  components: {
    RangeCategoryFilterItem,
  },
  mixins: [screen_mix],
  props: {
    item: Object,
    skeleton: Boolean,
    isHoverable: Boolean,
    useHex: Boolean,
    hasArrowBtn: Boolean,
    hasFilterBtn: Boolean,
    hasHands: { type: Boolean, default: true },
    hasDraws: { type: Boolean, default: true },
    hasEqBuckets: { type: Boolean, default: true },
    hasEqBucketsAdvanced: { type: Boolean, default: true },
    hasHandsTitle: Boolean,
    hasDrawsTitle: Boolean,
    hasEqBucketsTitle: Boolean,
    hasEqBucketsAdvancedTitle: Boolean,
    catHands: { default: () => [] },
    catDraws: { default: () => [] },
    catEqBuckets: { default: () => [] },
    catEqBucketsAdvanced: { default: () => [] },
    filterCategories: { default: () => [] },
    handInfo: Object,
    readOnly: Boolean,
  },
  emits: ['cat:mouseenter', 'cat:mouseleave', 'cat:click', 'cat:filterclick', 'cat:arrowclick'],
  data() {
    return {
      mouseover: false,
    }
  },
  methods: {
    handleMouseenter: throttle(function (item) {
      if (this.readOnly) return
      if (!this.mouseover) return
      this.$emit('cat:mouseenter', item)
    }, 40),
    handleContMouseenter() {
      this.mouseover = true
    },
    handleContMouseleave() {
      if (this.readOnly) return
      this.mouseover = false
      this.$emit('cat:mouseleave')
    },
    handleClick(item) {
      if (this.readOnly) return
      this.$emit('cat:click', item)
    },
    handleContextmenuClick(e, item) {
      if (this.hasFilterBtn) {
        e.preventDefault()
        e.stopPropagation()
        this.handleFilterClick(item)
      }
    },
    handleFilterClick(item) {
      if (this.readOnly) return
      this.$emit('cat:filterclick', item)
    },
    toggleRangeCatPlusPrevious(idx, categories) {
      if (this.readOnly) return
      const items = categories.filter((_, i) => i <= idx)
      this.$emit('cat:arrowclick', items)
    },
    getSkeletonItems() {
      const num = 1
      return [...Array(num).keys()].map((i, idx) => ({
        id: idx,
        name: '',
        players: [
          {
            total_occurrence: 0,
            solution: [],
          },
        ],
      }))
    },
    allPreviousIncluded(idx, categoryItems) {
      const items = categoryItems.filter((_, i) => i <= idx)
      const res = items.every(c => this.filterCategories.some(i => i.name === c.name))
      return res
    },
  },
  computed: {
    options() {
      const hands = getVisibleItems(this.catHands, this.useTotalOccurenceNormalized)
      const draws = getVisibleItems(this.catDraws, this.useTotalOccurenceNormalized)
      const eq_buckets = getVisibleItems(this.catEqBuckets, this.useTotalOccurenceNormalized)
      const eq_buckets_advanced = getVisibleItems(
        this.catEqBucketsAdvanced,
        this.useTotalOccurenceNormalized,
      )

      const res = [
        {
          id: 'hands',
          visible: !!(this.hasHands && (hands.length || this.skeleton)),
          titleVisible: this.hasHandsTitle,
          hand: !this.handInfo ? '' : getCategoriesHandIndex()[this.handInfo?.hand_category],
          items: this.hasHands ? hands : [],
        },
        {
          id: 'draws',
          visible: !!(this.hasDraws && (draws.length || this.skeleton)),
          titleVisible: this.hasDrawsTitle,
          hand: !this.handInfo ? '' : getCategoriesDrawsIndex()[this.handInfo?.draw_category],
          items: this.hasDraws ? draws : [],
        },
        {
          id: 'eq_buckets',
          visible: !!(this.hasEqBuckets && (eq_buckets.length || this.skeleton)),
          titleVisible: this.hasEqBucketsTitle,
          hand: !this.handInfo ? '' : getCategoriesEqBucketsIndex()[this.handInfo?.equity_buckets],
          items: this.hasEqBuckets ? eq_buckets : [],
        },
        {
          id: 'eq_buckets_advanced',
          visible: !!(this.hasEqBucketsAdvanced && (eq_buckets_advanced.length || this.skeleton)),
          titleVisible: this.hasEqBucketsAdvancedTitle,
          hand: !this.handInfo
            ? ''
            : getCategoriesEqBucketsAdvancedIndex()[this.handInfo?.equity_buckets_advanced],
          items: this.hasEqBucketsAdvanced ? eq_buckets_advanced : [],
        },
      ].filter(i => i.visible || i.titleVisible)
      // console.log(res, this.skeleton)
      // console.log({
      //   res,
      //   hasHands: this.hasHands,
      //   hasHandsTitle: this.hasHandsTitle,
      //   hasDraws: this.hasDraws,
      //   hasDrawsTitle: this.hasDrawsTitle,
      //   hasEqBuckets: this.hasEqBuckets,
      //   hasEqBucketsTitle: this.hasEqBucketsTitle,
      //   hasEqBucketsAdvanced: this.hasEqBucketsAdvanced,
      //   hasEqBucketsAdvancedTitle: this.hasEqBucketsAdvancedTitle,
      // })
      return res
    },
    useTotalOccurenceNormalized() {
      return this.$store.getters.sw_range_length === 'swv_range_length'
    },
  },
}
</script>
<style lang="scss" scoped>
.tracactr {
  width: 100%;
  display: flex;
  flex-direction: column;
  --gap: 0.5rem;
  --tracactr-item-height: 1.5rem;
  --tracactr-raswitch-opacity: 0.25;

  --clr-tracactr-item-active: var(--clr-white-0);
  --clr-tracactr-item-inactive: var(--clr-white-0);
  &_item {
    display: flex;
    flex-direction: column;
    margin: 0rem 0 var(--gap) 0;

    --hvr-top-content-opacity: var(--tracactr-raswitch-opacity);
    &:last-child {
      margin: 0;
    }
  }
  &_title {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--clr-grey-2);
    padding-bottom: 0.25rem;
    position: relative;
    display: flex;
    &_tag {
      right: -1rem;
      text-transform: none;
    }
  }
  &_category {
    display: flex;
    flex-direction: column;
    &_item {
      height: var(--tracactr-item-height);
    }
  }
}
</style>
